import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import API from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { ChatCircle, PaperPlaneTilt, Plus, At, UsersThree, Briefcase, UserCircle, Trash, PushPin, PushPinSlash } from "@phosphor-icons/react";
import { toast } from "sonner";

/* ============================================================
   Unified Messaging Page — three scopes in tabs:
     - Suppliers: admins ↔ suppliers (legacy /vendor-messages endpoints)
     - Admin Team: admin team room + 1:1 admin DMs (/team-messages scope=admin_*)
     - In-House: admins ↔ in-house operatives (/team-messages scope=in_house)
   Non-admins see only the tab(s) relevant to their role.
   ============================================================ */

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = Math.max(0, (Date.now() - d.getTime()) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return d.toLocaleDateString();
}

// Replace @<user-id> tokens with @Name (pull from participants or cached users)
function renderMention(body, nameMap) {
  if (!body) return body;
  const parts = body.split(/(@[a-fA-F0-9-]{24,36})/g);
  return parts.map((p, i) => {
    if (p.startsWith("@")) {
      const id = p.slice(1);
      const name = nameMap[id];
      if (name) return <span key={i} className="font-semibold text-primary">@{name}</span>;
    }
    return <span key={i}>{p}</span>;
  });
}

// Shared message composer with @mention autocomplete
function Composer({ value, onChange, onSend, sending, mentionable = [], placeholder = "Type a message…", disabled = false }) {
  const inputRef = useRef(null);
  const [suggest, setSuggest] = useState({ open: false, query: "", start: 0 });

  const handleChange = (e) => {
    const v = e.target.value;
    onChange(v);
    // Detect @mention trigger
    const caret = e.target.selectionStart || v.length;
    const before = v.slice(0, caret);
    const m = before.match(/(?:^|\s)@([^\s@]{0,30})$/);
    if (m) {
      setSuggest({ open: true, query: m[1].toLowerCase(), start: caret - m[1].length - 1 });
    } else {
      setSuggest({ open: false, query: "", start: 0 });
    }
  };

  const filtered = useMemo(() => {
    if (!suggest.open) return [];
    return mentionable
      .filter((u) => u.name.toLowerCase().includes(suggest.query) || u.email.toLowerCase().includes(suggest.query))
      .slice(0, 6);
  }, [mentionable, suggest]);

  const pick = (u) => {
    const before = value.slice(0, suggest.start);
    const after = value.slice(inputRef.current?.selectionStart || value.length);
    const token = `@${u.id} `;
    const next = before + token + after;
    onChange(next);
    setSuggest({ open: false, query: "", start: 0 });
    setTimeout(() => {
      inputRef.current?.focus();
      const pos = (before + token).length;
      inputRef.current?.setSelectionRange(pos, pos);
    }, 0);
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  return (
    <div className="relative">
      {suggest.open && filtered.length > 0 && (
        <div className="absolute bottom-full left-0 right-0 mb-2 border border-border rounded-md bg-card shadow-lg z-10 max-h-48 overflow-y-auto" data-testid="mention-suggest">
          {filtered.map((u) => (
            <button key={u.id} onClick={() => pick(u)} type="button"
                    className="w-full text-left px-3 py-2 hover:bg-muted text-xs flex items-center gap-2"
                    data-testid={`mention-option-${u.id}`}>
              <At className="h-3 w-3 text-muted-foreground" />
              <span className="font-medium">{u.name}</span>
              <span className="text-muted-foreground capitalize">{u.role?.replace("_", " ")}</span>
            </button>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2">
        <Input ref={inputRef} value={value} onChange={handleChange} onKeyDown={onKey}
               placeholder={placeholder} disabled={disabled || sending}
               data-testid="messages-composer-input" />
        <Button onClick={onSend} disabled={disabled || sending || !value.trim()}
                data-testid="messages-composer-send">
          <PaperPlaneTilt className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

/* ==================== Suppliers (legacy /vendor-messages) ==================== */

function SuppliersTab({ role }) {
  const [threads, setThreads] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [showNewChat, setShowNewChat] = useState(false);
  const msgEndRef = useRef(null);

  const fetchThreads = useCallback(async () => {
    try {
      const { data } = await API.get("/vendor-messages/threads");
      setThreads(data);
      if (role === "vendor" && data.length > 0 && !activeId) setActiveId(data[0].vendor_id);
    } catch {}
  }, [role, activeId]);

  const fetchVendors = useCallback(async () => {
    if (role === "vendor") return;
    try {
      const { data } = await API.get("/suppliers");
      setVendors(data);
    } catch {}
  }, [role]);

  const fetchMessages = useCallback(async (vendorId) => {
    setActiveId(vendorId);
    try {
      const { data } = await API.get(`/vendor-messages/${vendorId}`);
      setMessages(data);
      setTimeout(() => msgEndRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    } catch {}
  }, []);

  useEffect(() => { fetchThreads(); fetchVendors(); }, [fetchThreads, fetchVendors]);

  const send = async () => {
    if (!newMsg.trim() || !activeId) return;
    setSending(true);
    try {
      await API.post("/vendor-messages", { vendor_id: activeId, message: newMsg.trim() });
      setNewMsg("");
      fetchMessages(activeId);
      fetchThreads();
    } catch (e) {
      toast.error("Could not send");
    } finally { setSending(false); }
  };

  const startChat = async (vendorId) => {
    setShowNewChat(false);
    fetchMessages(vendorId);
  };

  const availableVendors = vendors.filter((v) => !threads.find((t) => t.vendor_id === v.id));

  return (
    <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-3 h-[calc(100vh-260px)] min-h-[400px]">
      <div className="border border-border rounded-md flex flex-col overflow-hidden bg-card" data-testid="suppliers-thread-list">
        <div className="px-3 py-2 border-b border-border flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Suppliers</span>
          {role !== "vendor" && (
            <Button size="sm" variant="ghost" onClick={() => setShowNewChat(true)} data-testid="suppliers-new-chat-btn">
              <Plus className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
        <div className="flex-1 overflow-y-auto">
          {threads.length === 0 ? (
            <div className="p-6 text-center text-xs text-muted-foreground">No conversations yet.</div>
          ) : threads.map((t) => (
            <button key={t.vendor_id} onClick={() => fetchMessages(t.vendor_id)}
                    className={`w-full text-left px-3 py-2.5 border-b border-border hover:bg-muted ${activeId === t.vendor_id ? "bg-muted" : ""}`}
                    data-testid={`suppliers-thread-${t.vendor_id}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-sm truncate">{t.vendor_name}</span>
                {t.unread_count > 0 && <Badge className="text-[10px] h-4 min-w-4 px-1">{t.unread_count}</Badge>}
              </div>
              <p className="text-xs text-muted-foreground truncate mt-0.5">{t.last_message}</p>
              <p className="text-[10px] text-muted-foreground/60 mt-0.5">{timeAgo(t.last_at)}</p>
            </button>
          ))}
        </div>
      </div>

      <div className="border border-border rounded-md flex flex-col overflow-hidden bg-card">
        {!activeId ? (
          <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
            <div className="text-center">
              <ChatCircle className="h-12 w-12 mx-auto mb-2 text-muted-foreground/40" />
              Select a conversation
            </div>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="suppliers-messages">
              {messages.map((m) => {
                const mine = m.sender_role === (role === "vendor" ? "vendor" : "admin");
                return (
                  <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[70%] rounded-lg px-3 py-2 ${mine ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                      {!mine && <p className="text-[10px] font-semibold opacity-70 mb-0.5">{m.sender_name}</p>}
                      <p className="text-sm whitespace-pre-wrap">{m.message}</p>
                      <p className="text-[9px] opacity-60 mt-1">{timeAgo(m.created_at)}</p>
                    </div>
                  </div>
                );
              })}
              <div ref={msgEndRef} />
            </div>
            <div className="p-3 border-t border-border">
              <Composer value={newMsg} onChange={setNewMsg} onSend={send} sending={sending} placeholder="Message this supplier…" />
            </div>
          </>
        )}
      </div>

      <Dialog open={showNewChat} onOpenChange={setShowNewChat}>
        <DialogContent>
          <DialogHeader><DialogTitle>Start conversation with a supplier</DialogTitle></DialogHeader>
          <div className="max-h-80 overflow-y-auto divide-y divide-border">
            {availableVendors.map((v) => (
              <button key={v.id} onClick={() => startChat(v.id)}
                      className="w-full text-left px-3 py-2 hover:bg-muted text-sm"
                      data-testid={`suppliers-new-${v.id}`}>
                {v.company_name || v.name}
              </button>
            ))}
            {availableVendors.length === 0 && <p className="p-4 text-xs text-muted-foreground">All suppliers already have a thread.</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ==================== Team Messages Tab (admin room, DMs, in-house) ==================== */

function TeamTab({ scope, title, emptyMsg, mentionable, users, role, userId, onUnreadChange }) {
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [pinned, setPinned] = useState([]);
  const [newMsg, setNewMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const isAdmin = ["super_admin", "admin", "project_manager"].includes(role);
  const msgEndRef = useRef(null);

  // Lookup map for mention rendering
  const nameMap = useMemo(() => {
    const m = {};
    (users || []).forEach((u) => { m[u.id] = u.name; });
    (mentionable || []).forEach((u) => { m[u.id] = u.name; });
    return m;
  }, [users, mentionable]);

  const fetchThreads = useCallback(async () => {
    try {
      const { data } = await API.get(`/team-messages/threads?scope=${scope}`);
      setThreads(data);
      // For single-thread scopes (admin_room, in_house-for-operative), auto-select
      if (data.length > 0 && !activeId) {
        if (scope === "admin_room" || (scope === "in_house" && role === "in_house")) {
          setActiveId(data[0].thread_id);
        }
      }
    } catch {}
  }, [scope, activeId, role]);

  const fetchMessages = useCallback(async (threadId) => {
    setActiveId(threadId);
    try {
      const [msgRes, pinRes] = await Promise.all([
        API.get(`/team-messages/thread/${threadId}`),
        API.get(`/team-messages/thread/${threadId}/pinned`).catch(() => ({ data: [] })),
      ]);
      setMessages(msgRes.data);
      setPinned(pinRes.data || []);
      setTimeout(() => msgEndRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
      onUnreadChange?.();
      fetchThreads();
    } catch {}
  }, [onUnreadChange, fetchThreads]);

  const togglePin = async (message, shouldPin) => {
    try {
      if (shouldPin) {
        await API.post(`/team-messages/${message.id}/pin`);
      } else {
        await API.delete(`/team-messages/${message.id}/pin`);
      }
      if (activeId) fetchMessages(activeId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update pin");
    }
  };

  useEffect(() => { fetchThreads(); }, [fetchThreads]);

  useEffect(() => { if (activeId) fetchMessages(activeId); // eslint-disable-next-line
  }, [activeId]);

  const send = async () => {
    if (!newMsg.trim()) return;
    setSending(true);
    try {
      const payload = { scope, message: newMsg.trim() };
      if (activeId && activeId !== "admin_room") payload.thread_id = activeId;
      await API.post("/team-messages", payload);
      setNewMsg("");
      if (activeId) fetchMessages(activeId);
      fetchThreads();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send");
    } finally { setSending(false); }
  };

  const startNew = async (peerUserId, peerName) => {
    setShowNew(false);
    setSending(true);
    try {
      const { data } = await API.post("/team-messages", {
        scope,
        peer_user_id: peerUserId,
        message: `👋 Hi ${peerName}`,
      });
      setActiveId(data.thread_id);
      fetchThreads();
    } catch (e) {
      toast.error("Could not start chat");
    } finally { setSending(false); }
  };

  const deleteThread = async (threadId) => {
    if (!window.confirm("Delete this entire conversation?")) return;
    try {
      await API.delete(`/team-messages/thread/${threadId}`);
      if (activeId === threadId) { setActiveId(null); setMessages([]); }
      fetchThreads();
    } catch (e) { toast.error("Could not delete"); }
  };

  const canStartNew = (scope === "admin_dm" || scope === "in_house") && role !== "in_house";
  const newPeerPool = (() => {
    if (scope === "admin_dm") return (mentionable || []).filter((u) => ["admin", "super_admin", "project_manager"].includes(u.role));
    if (scope === "in_house") return (mentionable || []).filter((u) => u.role === "in_house" && !threads.find((t) => t.operative_id === u.id));
    return [];
  })();

  const activeThread = threads.find((t) => t.thread_id === activeId);

  return (
    <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-3 h-[calc(100vh-300px)] min-h-[400px]">
      <div className="border border-border rounded-md flex flex-col overflow-hidden bg-card">
        <div className="px-3 py-2 border-b border-border flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</span>
          {canStartNew && (
            <Button size="sm" variant="ghost" onClick={() => setShowNew(true)} data-testid={`${scope}-new-btn`}>
              <Plus className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
        <div className="flex-1 overflow-y-auto">
          {threads.length === 0 ? (
            <div className="p-6 text-center text-xs text-muted-foreground">{emptyMsg}</div>
          ) : threads.map((t) => (
            <button key={t.thread_id} onClick={() => fetchMessages(t.thread_id)}
                    className={`w-full text-left px-3 py-2.5 border-b border-border hover:bg-muted ${activeId === t.thread_id ? "bg-muted" : ""}`}
                    data-testid={`${scope}-thread-${t.thread_id}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-sm truncate">{t.title}</span>
                {t.unread_count > 0 && <Badge className="text-[10px] h-4 min-w-4 px-1">{t.unread_count}</Badge>}
              </div>
              <p className="text-xs text-muted-foreground truncate mt-0.5">
                {t.last_sender_name ? `${t.last_sender_name}: ` : ""}{t.last_message || "(no messages yet)"}
              </p>
              <p className="text-[10px] text-muted-foreground/60 mt-0.5">{timeAgo(t.last_at)}</p>
            </button>
          ))}
        </div>
      </div>

      <div className="border border-border rounded-md flex flex-col overflow-hidden bg-card">
        {!activeId ? (
          <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
            <div className="text-center">
              <ChatCircle className="h-12 w-12 mx-auto mb-2 text-muted-foreground/40" />
              Select a conversation
            </div>
          </div>
        ) : (
          <>
            <div className="px-3 py-2 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <UserCircle className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="text-sm font-semibold truncate">{activeThread?.title || "Conversation"}</span>
              </div>
              {scope !== "admin_room" && role !== "in_house" && (
                <button onClick={() => deleteThread(activeId)} className="text-muted-foreground hover:text-destructive" title="Delete conversation"
                        data-testid={`${scope}-delete-thread`}>
                  <Trash className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid={`${scope}-messages`}>
              {pinned.length > 0 && (
                <div className="sticky top-0 -mt-4 -mx-4 px-4 pt-2 pb-3 bg-amber-50/80 dark:bg-amber-950/30 border-b border-amber-200 dark:border-amber-800 backdrop-blur-sm z-10 mb-2" data-testid={`${scope}-pinned-strip`}>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-300 flex items-center gap-1.5 mb-1.5">
                    <PushPin className="h-3 w-3" weight="fill" /> Pinned ({pinned.length})
                  </p>
                  <div className="space-y-1.5">
                    {pinned.map((p) => (
                      <div key={p.id} className="group flex items-start gap-2 text-xs bg-white/60 dark:bg-slate-900/40 rounded px-2 py-1.5 border border-amber-200/50 dark:border-amber-800/50" data-testid={`${scope}-pinned-${p.id}`}>
                        <PushPin className="h-3 w-3 text-amber-600 mt-0.5 shrink-0" weight="fill" />
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-slate-900 dark:text-slate-100 text-[11px]">{p.sender_name}</p>
                          <p className="text-slate-700 dark:text-slate-300 line-clamp-2">{renderMention(p.message, nameMap)}</p>
                        </div>
                        {isAdmin && (
                          <button onClick={() => togglePin(p, false)}
                                  className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-500 hover:text-rose-600"
                                  title="Unpin" data-testid={`${scope}-unpin-${p.id}`}>
                            <PushPinSlash className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m) => {
                const mine = m.sender_id === userId;
                const isPinned = !!m.pinned_at;
                return (
                  <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"} group`}>
                    <div className={`max-w-[70%] rounded-lg px-3 py-2 relative ${mine ? "bg-primary text-primary-foreground" : "bg-muted"} ${isPinned ? "ring-1 ring-amber-400" : ""}`}>
                      {!mine && <p className="text-[10px] font-semibold opacity-70 mb-0.5">{m.sender_name}</p>}
                      <p className="text-sm whitespace-pre-wrap">{renderMention(m.message, nameMap)}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <p className="text-[9px] opacity-60">{timeAgo(m.created_at)}</p>
                        {isPinned && (
                          <span className="text-[9px] inline-flex items-center gap-0.5 text-amber-600 dark:text-amber-400"><PushPin className="h-2.5 w-2.5" weight="fill" /> pinned</span>
                        )}
                      </div>
                      {isAdmin && (
                        <button onClick={() => togglePin(m, !isPinned)}
                                className={`absolute -top-2 -right-2 h-5 w-5 rounded-full bg-card border border-border shadow flex items-center justify-center text-slate-600 hover:text-amber-600 transition-opacity ${isPinned ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
                                title={isPinned ? "Unpin" : "Pin to top"}
                                data-testid={`${scope}-${isPinned ? "unpin" : "pin"}-msg-${m.id}`}>
                          <PushPin className="h-3 w-3" weight={isPinned ? "fill" : "regular"} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
              <div ref={msgEndRef} />
            </div>
            <div className="p-3 border-t border-border">
              <Composer value={newMsg} onChange={setNewMsg} onSend={send} sending={sending}
                        mentionable={mentionable}
                        placeholder="Type a message… use @ to mention someone" />
              <p className="text-[10px] text-muted-foreground mt-1 flex items-center gap-1"><At className="h-3 w-3" /> Tip: @mention someone to bring them into the chat</p>
            </div>
          </>
        )}
      </div>

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Start conversation</DialogTitle>
            <DialogDescription>Pick a {scope === "admin_dm" ? "teammate" : "operative"} to message.</DialogDescription>
          </DialogHeader>
          <div className="max-h-80 overflow-y-auto divide-y divide-border">
            {newPeerPool.length === 0 && <p className="p-4 text-xs text-muted-foreground">No one available.</p>}
            {newPeerPool.map((u) => (
              <button key={u.id} onClick={() => startNew(u.id, u.name)}
                      className="w-full text-left px-3 py-2 hover:bg-muted text-sm flex items-center gap-2"
                      data-testid={`${scope}-start-${u.id}`}>
                <UserCircle className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="font-medium">{u.name}</p>
                  <p className="text-[11px] text-muted-foreground capitalize">{u.role?.replace("_", " ")} · {u.email}</p>
                </div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ==================== Main page ==================== */

export default function MessagesPage() {
  const { user } = useAuth();
  const role = user?.role;
  const isAdmin = ["super_admin", "admin", "project_manager"].includes(role);
  const isVendor = role === "vendor";
  const isInHouse = role === "in_house";
  const [mentionable, setMentionable] = useState([]);
  const [adminSubTab, setAdminSubTab] = useState("admin_room");
  const [defaultTab, setDefaultTab] = useState("suppliers");

  useEffect(() => {
    if (isInHouse) setDefaultTab("in_house");
    else if (isVendor) setDefaultTab("suppliers");
    else setDefaultTab("admins");
  }, [isAdmin, isVendor, isInHouse]);

  const loadMentionable = useCallback(async () => {
    if (!isAdmin && !isInHouse) return;
    try {
      const { data } = await API.get("/team-messages/mentionable");
      setMentionable(data || []);
    } catch {}
  }, [isAdmin, isInHouse]);

  useEffect(() => { loadMentionable(); }, [loadMentionable]);

  return (
    <div className="space-y-4" data-testid="messages-page">
      <div className="flex items-center gap-3">
        <ChatCircle className="h-7 w-7 text-primary" />
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Messages</h1>
          <p className="text-xs text-muted-foreground">All your conversations in one place — suppliers, your admin team, and your in-house operatives.</p>
        </div>
      </div>

      <Tabs defaultValue={defaultTab} value={defaultTab} onValueChange={setDefaultTab} className="w-full">
        <TabsList className="mb-3" data-testid="messages-tabs">
          {(isAdmin || isVendor) && (
            <TabsTrigger value="suppliers" data-testid="messages-tab-suppliers">
              <Briefcase className="h-3.5 w-3.5 mr-1.5" /> Suppliers
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="admins" data-testid="messages-tab-admins">
              <UsersThree className="h-3.5 w-3.5 mr-1.5" /> Admin Team
            </TabsTrigger>
          )}
          {(isAdmin || isInHouse) && (
            <TabsTrigger value="in_house" data-testid="messages-tab-inhouse">
              <UserCircle className="h-3.5 w-3.5 mr-1.5" /> In-House
            </TabsTrigger>
          )}
        </TabsList>

        {(isAdmin || isVendor) && (
          <TabsContent value="suppliers">
            <SuppliersTab role={role} />
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="admins">
            <Tabs value={adminSubTab} onValueChange={setAdminSubTab} className="w-full">
              <TabsList className="mb-3">
                <TabsTrigger value="admin_room" data-testid="messages-sub-admin-room">Team Room</TabsTrigger>
                <TabsTrigger value="admin_dm" data-testid="messages-sub-admin-dm">Direct Messages</TabsTrigger>
              </TabsList>
              <TabsContent value="admin_room">
                <TeamTab scope="admin_room" title="Team Room" emptyMsg="No messages yet — kick off the team chat."
                         mentionable={mentionable} users={mentionable} role={role} userId={user?._id} />
              </TabsContent>
              <TabsContent value="admin_dm">
                <TeamTab scope="admin_dm" title="Direct Messages" emptyMsg="No DMs yet. Click + to start one."
                         mentionable={mentionable} users={mentionable} role={role} userId={user?._id} />
              </TabsContent>
            </Tabs>
          </TabsContent>
        )}

        {(isAdmin || isInHouse) && (
          <TabsContent value="in_house">
            <TeamTab scope="in_house"
                     title={isInHouse ? "Office" : "In-House Operatives"}
                     emptyMsg={isInHouse ? "No messages from the office yet." : "No operative chats yet. Click + to start one."}
                     mentionable={mentionable} users={mentionable} role={role} userId={user?._id} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
